import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { FaReply, FaPaperPlane, FaRegSmile, FaEllipsisH } from "react-icons/fa";
import { toast } from "react-hot-toast";
import InfiniteScroll from "react-infinite-scroll-component";
import EmojiPicker from "emoji-picker-react";

function CommentSection({ documentId }) {
  const { authState } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyContent, setReplyContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showReplyEmojiPicker, setShowReplyEmojiPicker] = useState({});

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/comments/get/${documentId}`,
        {
          headers: { "x-auth-token": authState.token },
          params: { page, limit: 10 },
        }
      );

      const commentsData = response.data;

      if (!Array.isArray(commentsData)) {
        throw new Error("Unexpected response structure");
      }

      const newComments = sortComments(commentsData);
      setComments((prevComments) =>
        page === 1 ? newComments : [...prevComments, ...newComments]
      );
      setHasMore(commentsData.length === 10);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching comments:", error);
      setLoading(false);
    }
  }, [documentId, authState.token, page]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const sortComments = (commentsArray) => {
    const commentMap = new Map();
    const rootComments = [];

    commentsArray.forEach((comment) => {
      commentMap.set(comment._id, { ...comment, replies: [], expanded: false });
    });

    commentsArray.forEach((comment) => {
      if (comment.parentCommentId) {
        const parentComment = commentMap.get(comment.parentCommentId);
        if (parentComment) {
          parentComment.replies.push(commentMap.get(comment._id));
        } else {
          rootComments.push(commentMap.get(comment._id));
        }
      } else {
        rootComments.push(commentMap.get(comment._id));
      }
    });

    return rootComments;
  };

  const handleAddComment = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/comments/create`,
        { documentId, content: newComment },
        {
          headers: { "x-auth-token": authState.token },
        }
      );
      setComments((prevComments) => [response.data, ...prevComments]);
      setNewComment("");
      toast.success("Comment added successfully!");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment. Please try again.");
    }
  };

  const handleAddReply = async (parentCommentId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/comments/create`,
        { documentId, content: replyContent[parentCommentId], parentCommentId },
        {
          headers: { "x-auth-token": authState.token },
        }
      );
      setComments((prevComments) =>
        updateCommentsWithNewReply(prevComments, response.data)
      );
      setReplyContent((prev) => ({ ...prev, [parentCommentId]: "" }));
      toast.success("Reply added successfully!");
    } catch (error) {
      console.error("Error adding reply:", error);
      toast.error("Failed to add reply. Please try again.");
    }
  };

  const updateCommentsWithNewReply = (comments, newReply) => {
    return comments.map((comment) => {
      if (comment._id === newReply.parentCommentId) {
        return {
          ...comment,
          replies: [
            ...(Array.isArray(comment.replies) ? comment.replies : []),
            newReply,
          ],
        };
      } else if (Array.isArray(comment.replies) && comment.replies.length > 0) {
        return {
          ...comment,
          replies: updateCommentsWithNewReply(comment.replies, newReply),
        };
      }
      return comment;
    });
  };

  const toggleExpand = (commentId) => {
    setComments((prevComments) =>
      toggleCommentExpansion(prevComments, commentId)
    );
  };

  const toggleCommentExpansion = (comments, commentId) => {
    return comments.map((comment) => {
      if (comment._id === commentId) {
        return { ...comment, expanded: !comment.expanded };
      } else if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: toggleCommentExpansion(comment.replies, commentId),
        };
      }
      return comment;
    });
  };

  const onEmojiClick = (emojiData, commentId = null) => {
    if (commentId) {
      setReplyContent((prev) => ({
        ...prev,
        [commentId]: (prev[commentId] || "") + emojiData.emoji,
      }));
      setShowReplyEmojiPicker((prev) => ({ ...prev, [commentId]: false }));
    } else {
      setNewComment((prevComment) => prevComment + emojiData.emoji);
      setShowEmojiPicker(false);
    }
  };

  const renderComment = (comment, level = 0) => (
    <li key={comment._id} className={`mb-2 ${level > 0 ? "ml-3" : ""}`}>
      <div className="bg-white rounded-lg shadow-sm p-2 text-xs">
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center">
            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold mr-2">
              {comment.userId && comment.userId.firstname
                ? comment.userId.firstname[0].toUpperCase()
                : "?"}
            </div>
            <span className="font-medium text-gray-800">
              {comment.userId
                ? `${comment.userId.firstname || ""} ${
                    comment.userId.lastname || ""
                  }`
                : "Anonymous"}
            </span>
          </div>
          <button
            onClick={() =>
              setReplyContent((prev) => ({
                ...prev,
                [comment._id]: prev[comment._id] || "",
              }))
            }
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <FaReply />
          </button>
        </div>
        <p className="text-gray-600 mb-1 line-clamp-2">{comment.content}</p>
        {comment.content && comment.content.length > 100 && (
          <button
            onClick={() => toggleExpand(comment._id)}
            className="text-blue-500 hover:text-blue-700 focus:outline-none text-xs"
          >
            {comment.expanded ? "Show less" : "Show more"}
          </button>
        )}
        <div className="flex justify-between items-center text-xxs text-gray-400 mt-1">
          <span>
            {comment.createdAt
              ? new Date(comment.createdAt).toLocaleString()
              : "Unknown date"}
          </span>
          <FaEllipsisH className="cursor-pointer hover:text-gray-600" />
        </div>
        {replyContent[comment._id] !== undefined && (
          <div className="mt-2 relative">
            <textarea
              placeholder="Write a reply..."
              value={replyContent[comment._id]}
              onChange={(e) =>
                setReplyContent((prev) => ({
                  ...prev,
                  [comment._id]: e.target.value,
                }))
              }
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 text-xs"
              rows="3"
            />
            <div className="absolute right-2 bottom-2 flex items-center">
              <button
                onClick={() =>
                  setShowReplyEmojiPicker((prev) => ({
                    ...prev,
                    [comment._id]: !prev[comment._id],
                  }))
                }
                className="text-gray-400 hover:text-gray-600 mr-2"
              >
                <FaRegSmile />
              </button>
              <button
                onClick={() => handleAddReply(comment._id)}
                className="text-blue-500 hover:text-blue-600"
              >
                <FaPaperPlane />
              </button>
            </div>
            {showReplyEmojiPicker[comment._id] && (
              <div className="relative right-0 bottom-full mb-1 z-10">
                <EmojiPicker
                  onEmojiClick={(emojiData) =>
                    onEmojiClick(emojiData, comment._id)
                  }
                  width={200}
                  height={350}
                  emojiStyle={{ fontSize: 2 }}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {comment.replies && comment.replies.length > 0 && (
        <ul className="mt-1 border-l border-gray-200">
          {comment.replies.map((reply) => renderComment(reply, level + 1))}
        </ul>
      )}
    </li>
  );

  return (
    <div className="comments-section w-72 bg-gray-50 rounded-lg shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-gray-800 to-pink-500 p-3">
        <h3 className="text-sm font-bold text-white">Comments</h3>
      </div>
      <div className="p-3">
        <div className="mb-3 relative">
          <textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="w-full p-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-400 text-xs"
            rows="4"
          />
          <div className="absolute right-2 bottom-2 flex items-center">
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="text-orange-300 hover:text-gray-600 mr-2"
            >
              <FaRegSmile />
            </button>
            <button
              onClick={handleAddComment}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:from-purple-600 hover:to-pink-600"
            >
              <FaPaperPlane className="text-xs" />
            </button>
          </div>
          {showEmojiPicker && (
            <div className="relative right-0 bottom-full mb-1 z-10">
              <EmojiPicker
                onEmojiClick={(emojiData) => onEmojiClick(emojiData)}
                width={265}
                height={350}
              />
            </div>
          )}
        </div>
        <div className="max-h-64 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <InfiniteScroll
            dataLength={comments.length}
            next={() => setPage((prevPage) => prevPage + 1)}
            hasMore={hasMore}
            loader={<div className="text-center text-xs py-2">Loading...</div>}
            endMessage={
              <p className="text-center text-xs text-gray-500 py-2">
                No more comments.
              </p>
            }
            scrollableTarget="scrollableDiv"
          >
            <ul className="space-y-2">
              {comments.map((comment) => renderComment(comment))}
            </ul>
          </InfiniteScroll>
        </div>
      </div>
      {loading && comments.length === 0 && (
        <div className="flex justify-center items-center h-16">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      )}
    </div>
  );
}

export default CommentSection;
